import { Assert } from "../../Assert";
import { FomanticShim, PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32, PropertyString } from "../../RadObject";
import { ApplicationInformationService } from "../../RadWindow";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ServiceManager } from "../../ServiceManager";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { SvgHelper } from "../SvgHelper";
import { NAMESPACE } from "./_Namespace";
import { IControl } from "./Control";
import { ActionButtonMixin, ActionButtonMixinPropertyValues, IActionButtonMixin } from "./Mixins/ActionButtonMixin";
import { Toolbar } from "./Toolbar";
import { IToolbarItem, ToolbarItem, ToolbarItemPropertyValues } from "./ToolbarItem";

import "./ToolbarButton.scss";

export enum ToolbarButtonModeEnum
{
    normal = "normal",
    toggle = "toggle",
}

export type ToolbarButtonPropertyValues =
    ToolbarItemPropertyValues
    & ActionButtonMixinPropertyValues
    & {
        alertCount: number;
        icon: string;
        iconInline: boolean;
        image: string;
        mode: ToolbarButtonModeEnum;
        showAlert: boolean;
        state: boolean;
        svgKey: string;
        toggleGroup: string;
        toggleGroupSelectionMandatory: boolean;
    };

export type ToolbarButtonProperties = {
    [name in keyof ToolbarButtonPropertyValues]: Property<ToolbarButtonPropertyValues[name]>
};

export interface IToolbarButton extends IToolbarItem, ToolbarButtonProperties, IActionButtonMixin
{
}

export class ToolbarButton extends ToolbarItem implements IToolbarButton
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ToolbarButton";
    public static override className = "ToolbarButton";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ToolbarButtonPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                mode: ToolbarButtonModeEnum.normal,
                state: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _alertElementRef!: ElementRef;
    protected _iconElementRef!: ElementRef;
    protected _imageElementRef!: ElementRef;
    protected _svgWrapperElementRef!: ElementRef;
    protected _textElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public alertCount!: PropertyInt32;
    public icon!: PropertyString;
    public iconInline!: PropertyBoolean;
    public image!: PropertyString;
    public mode!: Property<ToolbarButtonModeEnum>;
    public showAlert!: PropertyBoolean;
    public state!: PropertyBoolean;
    public svgKey!: PropertyString;
    public toggleGroup!: PropertyString;
    public toggleGroupSelectionMandatory!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ToolbarButtonPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Protected Methods

    protected _alertCountChanged(change: PropertyChange<number>)
    {
        Assert.isNotNullOrUndefined(this._alertElementRef, "this._alertElementRef");

        if (change.currentValue !== null)
            this._alertElementRef.current.text(change.currentValue < 0 ? "0" : change.currentValue > 99 ? "99+" : change.currentValue.toString());
        else
            this._alertElementRef.current.text("!");

        if (change.currentValue !== null && change.currentValue > (change.originalValue ? change.originalValue : 0))
            this._pulseAlert();
    }
    protected _iconChanged(change: PropertyChange<string>)
    {
        Assert.isNotNullOrUndefined(this._iconElementRef, "this._iconElementRef");

        if (change.originalValue)
            this._iconElementRef.current.removeClass(change.originalValue);

        if (change.currentValue)
        {
            this._iconElementRef.current.addClass(change.currentValue);
            this._iconElementRef.current.show();
        }
        else
            this._iconElementRef.current.hide();
    }
    protected _iconInlineChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-style-icon-inline");
        else
            this.removeCssClass("rad-style-icon-inline");
    }
    protected _imageChanged(change: PropertyChange<string>)
    {
        if (change.currentValue)
            this._imageElementRef.current.attr("src", change.currentValue.startsWith("/") ? StringHelper.format("{0}{1}", ServiceManager.getRequired(ApplicationInformationService).virtualDirectory, change.currentValue.substring(1)) : change.currentValue).show();
        else
            this._imageElementRef.current.hide();
    }
    protected _modeChanged(change: PropertyChange<ToolbarButtonModeEnum>)
    {
        /* Intentionally empty */
    }
    protected _pulseAlert()
    {
        FomanticShim(this._alertElementRef.current).transition("flash");
    }
    protected _showAlertChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this._alertElementRef.current.css({ display: "block" });
        else
            this._alertElementRef.current.css({ display: "none" });
    }
    protected _stateChanged(change: PropertyChange<boolean>)
    {
        const
            toggleGroup = this.toggleGroup.get();

        let
            toolbar;

        if (change.currentValue && toggleGroup)
        {
            toolbar = this.parentControlGet() as Toolbar | undefined;

            if (toolbar)
            {
                toolbar.controlsGet()
                    .filter(item =>
                    {
                        return (
                            item !== this
                            && item instanceof ToolbarButton
                            && item.mode.get() === ToolbarButtonModeEnum.toggle
                            && item.toggleGroup.get() === toggleGroup
                        );
                    })
                    .map(item => item as ToolbarButton)
                    .forEach(item => { item.state.set(false); });
            }
        }

        if (change.currentValue)
            this.addCssClass("rad-state-on");
        else
            this.removeCssClass("rad-state-on");

        this.triggerHandler("stateChanged", change);
    }
    protected _svgKeyChanged(change: PropertyChange<string>)
    {
        Assert.isNotNullOrUndefined(this._svgWrapperElementRef, "this._svgWrapperElementRef");

        if (change.currentValue)
        {
            this._svgWrapperElementRef.current.empty();
            this._svgWrapperElementRef.current.append(SvgHelper.createSvg(change.currentValue)).show();
        }
        else
            this._svgWrapperElementRef.current.hide();
    }
    protected _toggleGroupChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _toggleGroupSelectionMandatoryChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ToolbarButtonPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._alertElementRef = new ElementRef();
        this._iconElementRef = new ElementRef();
        this._imageElementRef = new ElementRef();
        this._svgWrapperElementRef = new ElementRef();
        this._textElementRef = new ElementRef();

        this.alertCount = new PropertyInt32(this, propertyValues.alertCount, this._alertCountChanged);
        this.icon = new PropertyString({
            context: this,
            initialValue: propertyValues.icon,
            changeHandler: this._iconChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Icon" }),
            ],
        });
        this.iconInline = new PropertyBoolean(this, propertyValues.iconInline, this._iconInlineChanged);
        this.image = new PropertyString({
            context: this,
            initialValue: propertyValues.image,
            changeHandler: this._imageChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Image" }),
            ],
        });
        this.mode = new Property<ToolbarButtonModeEnum>({
            context: this,
            dotNetType: "System.String, System.Private.CoreLib",
            initialValue: propertyValues.mode,
            changeHandler: this._modeChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Behavior" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Mode" }),
            ],
        });
        this.showAlert = new PropertyBoolean(this, propertyValues.showAlert, this._showAlertChanged);
        this.state = new PropertyBoolean(this, propertyValues.state, this._stateChanged);
        this.svgKey = new PropertyString(this, propertyValues.svgKey, this._svgKeyChanged);
        this.toggleGroup = new PropertyString(this, propertyValues.toggleGroup, this._toggleGroupChanged);
        this.toggleGroupSelectionMandatory = new PropertyBoolean(this, propertyValues.toggleGroupSelectionMandatory, this._toggleGroupSelectionMandatoryChanged);

        this.action = new PropertyString(this, propertyValues.action, this._actionChanged);
        this.actionArguments = new PropertyString(this, propertyValues.actionArguments, this._actionArgumentsChanged);
        this.actionTarget = new Property<IControl>(this, "System.Object, System.Private.CoreLib", propertyValues.actionTarget, this._actionTargetChanged);
        this.actionTargetControlClass = new PropertyString(this, propertyValues.actionTargetControlClass, this._actionTargetControlClassChanged);
        this.actionTargetId = new PropertyString(this, propertyValues.actionTargetId, this._actionTargetIdChanged);
        this.actionTargetSelector = new PropertyString(this, propertyValues.actionTargetSelector, this._actionTargetSelectorChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.on("mousedown touchstart", { self: this }, (evt: IJNativeEvent) => { evt.stopPropagation(); });
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this._svgWrapperElementRef.current.hide();
        this._imageElementRef.current.hide();
        this._iconElementRef.current.hide();

        return $$element;
    }
    protected override _createElementComplete()
    {
        super._createElementComplete();

        this.addCssClass("rad-control-toolbar-button");

        // Trigger Property Changes
        this.alertCount.triggerChange();
        this.iconInline.triggerChange();
        this.icon.triggerChange();
        this.image.triggerChange();
        this.showAlert.triggerChange();
        this.text.triggerChange();
        this.state.triggerChange();
        this.svgKey.triggerChange();
    }
    protected override _createElementHtml(children?: JsxFragment[])
    {
        return (
            <a>
                <span ref={this._containerElementRef} class="rad-container">
                    <span ref={this._svgWrapperElementRef} class="rad-svg-wrapper"></span>
                    <img ref={this._imageElementRef}></img>
                    <i ref={this._iconElementRef} class="icon"></i>
                    <span ref={this._textElementRef} class="rad-text"></span>
                    {children}
                </span>
                <span ref={this._alertElementRef} class="rad-alert"></span>
            </a>
        );
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        const
            $$element = this.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");
        Assert.isNotNullOrUndefined(this._textElementRef, "this._textElementRef");

        super._textChanged(change);

        this._textElementRef.current.text(change.currentValue);

        if (change.currentValue)
            $$element.attr("title", change.currentValue);
        else
            $$element.removeAttr("title");
    }

    // #endregion

    // #region IActionButtonMixin

    // #region Properties

    public action!: PropertyString;
    public actionArguments!: PropertyString;
    public actionTarget!: Property<IControl>;
    public actionTargetControlClass!: PropertyString;
    public actionTargetId!: PropertyString;
    public actionTargetSelector!: PropertyString;

    // #endregion

    // #region Methods

    protected _actionChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _actionArgumentsChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _actionTargetChanged(change: PropertyChange<IControl>)
    {
        /* Intentionally empty */
    }
    protected _actionTargetControlClassChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _actionTargetIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _actionTargetSelectorChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #endregion

    // #region Event Handlers

    protected override onClick(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.focus(); // Ensure button has focus (causes other changed events to fire)

        ActionButtonMixin.processActionButtonClick(self, evt);

        if (self.mode.get() === ToolbarButtonModeEnum.toggle)
        {
            if (!self.toggleGroupSelectionMandatory.get() || !self.state.get())
                self.state.set(!self.state.get());
        }
    }

    // #endregion

    // #endregion
}

